"use client";

import "@/components/home/spectrum-hero.css";
import "@/components/ui/atoms/categories/layout.css";
import "@/components/ui/molecules/categories/page-chrome.css";
import { MarketingNav } from "@/components/home/marketing-nav";
import { Button } from "@/components/ui/shadcn/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/shadcn/dialog";
import { useSession } from "@/lib/auth/client";
import { routes } from "@/lib/routing/routes";
import { heroSerif } from "@/lib/theme/hero-serif";
import { ArrowRight, Video } from "lucide-react";

import Image from "next/image";
import Link from "next/link";
import { memo, useEffect, useRef, useState } from "react";

export const SpectrumHero = memo(function SpectrumHero() {
    const { data: session } = useSession();
    const isAuthenticated = !!session?.user;

    // The in-hero nav recedes during the scroll pop-out, so a fixed twin
    // slides back in once the hero has fully scrolled away.
    const rootRef = useRef<HTMLDivElement>(null);
    const [pastHero, setPastHero] = useState(false);
    const [teaserOpen, setTeaserOpen] = useState(false);

    useEffect(() => {
        if (!teaserOpen) {
            return;
        }
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                setTeaserOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [teaserOpen]);

    useEffect(() => {
        const hero = rootRef.current;
        if (!hero) {
            return;
        }
        const observer = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting));
        observer.observe(hero);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={rootRef} className={`dw-home-spectrum-hero ${heroSerif.variable}`} data-component="SpectrumHero" data-location="src/components/home/spectrum-hero.tsx">
            <div className="dw-home-spectrum-hero__sticky">
                <MarketingNav className="dw-home-spectrum-hero__nav" isAuthenticated={isAuthenticated} priority />

                <section className="dw-home-spectrum-hero__card dw-marketing-card">
                    {
                        /* Backdrop wrapper owns the rounded clipping so the card itself can
            let the product shot escape its bounds during the scroll pop-out. */
                    }
                    <div className="dw-home-spectrum-hero__backdrop" aria-hidden>
                        <Image src="/graphics/dw-spectrum-bg.webp" alt="" aria-hidden fill sizes="100vw" className="dw-home-spectrum-hero__bg" priority />
                        <div className="dw-home-spectrum-hero__fade" aria-hidden />
                    </div>

                    <div className="dw-home-spectrum-hero__content">
                        <h1 className="dw-home-spectrum-hero__headline dw-text-hero-lg">The most advanced dispute platform ever built.</h1>
                        <p className="dw-text-lead">
                            Built to make your business extraordinarily productive. <br />
                            Dispute, Metro 2®, Fluid, and tracked mail in one system.
                        </p>
                        <div className="dw-home-spectrum-hero__cta-row">
                            <Button asChild size="lg">
                                <Link href={isAuthenticated ? routes.crm.dashboard : routes.crm.signup}>
                                    <ArrowRight aria-hidden />
                                    {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
                                </Link>
                            </Button>
                            <Button variant="secondary" size="lg" type="button" onClick={() => setTeaserOpen(true)}>
                                <Video aria-hidden />
                                Watch the teaser
                            </Button>
                        </div>
                    </div>

                    <div className="dw-home-spectrum-hero__shot-wrap">
                        <Image src="/graphics/dw-shot-dispute-rounds-light.png" alt="A client's Disputes tab in light mode with Round 3 ready for review, bureau score changes, and Fluid's completed workflow steps" width={2753} height={2171} sizes="(min-width: 1440px) 1296px, 90vw" className="dw-home-spectrum-hero__shot" priority />
                    </div>
                </section>
            </div>

            <MarketingNav
                className="dw-home-spectrum-hero__float-nav dw-bar"
                visible={pastHero}
                isAuthenticated={isAuthenticated}
                inert={!pastHero}
            />

            <Dialog open={teaserOpen} onOpenChange={setTeaserOpen}>
                <DialogContent size="xl" closeLabel="Close video">
                    <DialogTitle className="dw-sr-only">DisputeWell teaser video</DialogTitle>
                    <video className="dw-home-spectrum-hero__teaser-player" src="/videos/dw-teaser.mp4" autoPlay controls playsInline />
                </DialogContent>
            </Dialog>
        </div>
    );
});
